import { AxiosError } from "axios";
import {
    DownloadNotification,
    EventAlertNotification,
    NotificationItem,
    SensorAlertNotification,
} from "components/notification/types";
import AxiosInstance from "./axiosInstance";
import { ApiResult, BaseService } from "./baseService";

interface NotificationDto {
    id: string;
    time: number;
    expires: number;
    event: string;
    topic: string;
    title: string;
    message: string;
    priority: number;
    tags: string[];
}

// Field mapping for line-based parsing
interface FieldParser<T> {
    prefix: string;
    mapToField: (value: string) => Partial<T>;
}

// Type-safe notification handler
interface NotificationHandler<T extends NotificationItem> {
    tag: string;
    type: T["type"];
    decode: (message: string, tags: string[]) => T["info"];
}

// Helper to extract name and UUID from format: "Name (UUID: uuid-value)"
const parseNameUuid = (value: string) => {
    // Use bounded quantifiers to prevent ReDoS via backtracking
    // [^(]{1,200} matches name (1-200 chars, anything except opening paren)
    // \s{1,20} limits whitespace to prevent backtracking
    // [^)]{1,100} matches UUID (1-100 chars, anything except closing paren)
    const match = /([^(]{1,200})\s{1,20}\(UUID:\s{0,20}([^)]{1,100})\)/.exec(
        value
    );
    return match ? { name: match[1].trim(), uuid: match[2].trim() } : null;
};

const removeSAIDFromSensorName = (value: string) =>
    value.replace(/\s{0,20}\(SAID:\s{0,20}[^)]{1,20}\)\s{0,20}$/i, "").trim();

// Field parsers for each notification type
const SENSOR_FIELD_PARSERS: FieldParser<SensorAlertNotification["info"]>[] = [
    { prefix: "- Condition:", mapToField: (v) => ({ condition: v }) },
    {
        prefix: "- Sensor:",
        mapToField: (v) => ({ sensorName: removeSAIDFromSensorName(v) }),
    },
    { prefix: "- Type:", mapToField: (v) => ({ sensorType: v }) },
    {
        prefix: "- Gateway:",
        mapToField: (v) => {
            const parsed = parseNameUuid(v);
            return parsed
                ? { gatewayName: parsed.name, gatewayUuid: parsed.uuid }
                : {};
        },
    },
    {
        prefix: "- LDSU:",
        mapToField: (v) => {
            const parsed = parseNameUuid(v);
            return parsed
                ? { ldsuName: parsed.name, ldsuUuid: parsed.uuid }
                : {};
        },
    },
    { prefix: "- Alert:", mapToField: (v) => ({ alertType: v }) },
    { prefix: "- Value:", mapToField: (v) => ({ alertValue: v }) },
];

/** According to the message format, alert message is the first block of the message. */
const extractSensorAlertMessage = (message: string): string => {
    const trimmed = message.trim();
    const segments = trimmed.split(/\n\n/);
    return (segments[0] ?? "").trim();
};

const DOWNLOAD_FIELD_PARSERS: FieldParser<DownloadNotification["info"]>[] = [
    { prefix: "URL:", mapToField: (v) => ({ downloadLink: v }) },
    { prefix: "Password:", mapToField: (v) => ({ password: v }) },
    { prefix: "Expiry Time:", mapToField: (v) => ({ expiryTime: v }) },
    { prefix: "Requested Time:", mapToField: (v) => ({ requestedTime: v }) },
];

const parseLines = <T>(
    message: string,
    parserMap: Map<string, FieldParser<T>>
): T => {
    const lines = message
        .trim()
        .split("\n")
        .filter((line) => line.trim());

    const result: Partial<T> = {};

    for (const line of lines) {
        const trimmedLine = line.trim();
        // Extract prefix (up to and including the colon)
        const colonIndex = trimmedLine.indexOf(":");
        if (colonIndex !== -1) {
            const prefix = trimmedLine.substring(0, colonIndex + 1);
            const parser = parserMap.get(prefix);
            if (parser) {
                const value = trimmedLine.substring(colonIndex + 1).trim();
                Object.assign(result, parser.mapToField(value));
            }
        }
    }

    return result as T;
};

const buildParserMap = <T>(
    parsers: FieldParser<T>[]
): Map<string, FieldParser<T>> => {
    return new Map(parsers.map((parser) => [parser.prefix, parser]));
};

const decodeSensorAlert = (
    message: string
): SensorAlertNotification["info"] => {
    return {
        ...parseLines(message, buildParserMap(SENSOR_FIELD_PARSERS)),
        alertMessage: extractSensorAlertMessage(message),
    };
};

const decodeDownload = (message: string): DownloadNotification["info"] => {
    return parseLines(message, buildParserMap(DOWNLOAD_FIELD_PARSERS));
};

const decodeEvent = (
    message: string,
    tags: string[]
): EventAlertNotification["info"] => {
    return {
        message,
        eventId: tags?.[0]?.split(":")?.[1] ?? "",
    };
};

// Individual handlers with specific types
const sensorHandler: NotificationHandler<SensorAlertNotification> = {
    tag: "alert",
    type: "sensor",
    decode: decodeSensorAlert,
};

const downloadHandler: NotificationHandler<DownloadNotification> = {
    tag: "takeout",
    type: "download",
    decode: decodeDownload,
};

const eventHandler: NotificationHandler<EventAlertNotification> = {
    tag: "event",
    type: "event",
    decode: decodeEvent,
};

// TypeScript infers the union type automatically!
const NOTIFICATION_HANDLERS = [
    sensorHandler,
    downloadHandler,
    eventHandler,
] as const;
export class NotificationService extends BaseService {
    private static instance: NotificationService;
    private readonly handlerMap: Map<
        string,
        typeof NOTIFICATION_HANDLERS[number]
    >;

    private constructor() {
        super();
        this.handlerMap = new Map(NOTIFICATION_HANDLERS.map((h) => [h.tag, h]));
    }

    private readonly getNotificationURL = (userBase64: string) => {
        return `/notification/${userBase64}?poll=1`;
    };

    private decodeNotification(dto: NotificationDto): NotificationItem | null {
        const tag = dto.tags?.[0]?.split(":")?.[0] ?? "";
        const handler = this.handlerMap.get(tag);

        if (!handler) {
            return null;
        }

        try {
            const info = handler.decode(dto.message, dto.tags);
            if (!info) {
                return null;
            }

            return {
                id: dto.id,
                time: dto.time,
                expires: dto.expires,
                event: dto.event,
                topic: dto.topic,
                title: dto.title,
                message: dto.message,
                priority: dto.priority,
                orgId: dto.tags?.[1]?.split(":")?.[1] ?? "",
                type: handler.type,
                info,
            } as NotificationItem;
        } catch {
            return null;
        }
    }

    private convertDTOtoNotiItem(
        dtoList: NotificationDto[]
    ): NotificationItem[] {
        const sortedDtos = [...dtoList].sort((a, b) => b.time - a.time);

        return sortedDtos
            .map((dto) => this.decodeNotification(dto))
            .filter((item): item is NotificationItem => item !== null);
    }

    private parseResponseData(responseData: string): NotificationDto[] {
        return responseData
            .trim()
            .split("\n")
            .filter((line) => line.trim())
            .map((line) => {
                try {
                    return JSON.parse(line) as NotificationDto;
                } catch {
                    return null;
                }
            })
            .filter((dto): dto is NotificationDto => dto !== null);
    }

    static getInstance(): NotificationService {
        if (!NotificationService.instance) {
            NotificationService.instance = new NotificationService();
        }
        return NotificationService.instance;
    }

    async getNotifications(
        userEmail: string
    ): Promise<ApiResult<NotificationItem[]>> {
        try {
            // Limit the quantifier to prevent ReDoS - base64 padding is max 2 characters
            const encodedEmail = btoa(userEmail);
            const cleanedEmail = encodedEmail.endsWith("=")
                ? encodedEmail.replace(/={1,2}$/, "")
                : encodedEmail;
            const response = await AxiosInstance.get(
                this.getNotificationURL(cleanedEmail)
            );
            const responseDataString =
                typeof response.data === "string"
                    ? response.data
                    : JSON.stringify(response.data);
            const dtos = this.parseResponseData(responseDataString);
            const notificationItems = this.convertDTOtoNotiItem(dtos);

            return this.handleSuccess<NotificationItem[]>({
                ...response,
                data: notificationItems,
            });
        } catch (error) {
            return this.handleError(error as AxiosError);
        }
    }
}

export const notificationService = NotificationService.getInstance();
